import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const BASE_URL = '/api';

// Notification types
export type NotificationType =
  | 'load_status'
  | 'driver_status'
  | 'document_required'
  | 'compliance_alert'
  | 'payment_received'
  | 'invoice_overdue'
  | 'maintenance_due'
  | 'system_alert'
  | 'message'
  | 'mention'
  | 'schedule_deviation'
  | 'eta_update';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';
export type NotificationStatus = 'unread' | 'read' | 'archived';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  priority: NotificationPriority;
  status: NotificationStatus;
  link?: string;
  metadata?: {
    loadId?: string;
    loadNumber?: string;
    driverId?: string;
    driverName?: string;
    customerId?: string;
    customerName?: string;
    invoiceId?: string;
    documentId?: string;
    amount?: number;
  };
  actionRequired?: boolean;
  actions?: {
    label: string;
    action: string;
    variant?: 'default' | 'destructive' | 'outline';
  }[];
  createdAt: string;
  readAt?: string;
  expiresAt?: string;
}

export interface NotificationPreferences {
  email: {
    enabled: boolean;
    loadUpdates: boolean;
    driverAlerts: boolean;
    complianceAlerts: boolean;
    paymentNotifications: boolean;
    dailyDigest: boolean;
  };
  push: {
    enabled: boolean;
    urgentOnly: boolean;
  };
  inApp: {
    enabled: boolean;
    sound: boolean;
  };
  quietHours: {
    enabled: boolean;
    start: string; // HH:MM format
    end: string;
  };
}

export interface NotificationStats {
  unreadCount: number;
  totalCount: number;
  byType: Record<NotificationType, number>;
  byPriority: Record<NotificationPriority, number>;
  actionRequired: number;
}

export interface NotificationQueryParams {
  status?: NotificationStatus;
  type?: NotificationType;
  priority?: NotificationPriority;
  actionRequired?: boolean;
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Fetch functions
async function fetchNotifications(params: NotificationQueryParams): Promise<PaginatedResponse<Notification>> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.type) searchParams.set('type', params.type);
  if (params.priority) searchParams.set('priority', params.priority);
  if (params.actionRequired !== undefined) searchParams.set('actionRequired', params.actionRequired.toString());
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());

  const res = await fetch(`${BASE_URL}/notifications?${searchParams.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch notifications');
  return res.json();
}

async function fetchNotificationStats(): Promise<NotificationStats> {
  const res = await fetch(`${BASE_URL}/notifications/stats`);
  if (!res.ok) throw new Error('Failed to fetch notification stats');
  return res.json();
}

async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const res = await fetch(`${BASE_URL}/notifications/preferences`);
  if (!res.ok) throw new Error('Failed to fetch notification preferences');
  return res.json();
}

// Query hooks
export function useNotifications(params: NotificationQueryParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => fetchNotifications(params),
    refetchInterval: 30000, // Refetch every 30 seconds
    enabled: options?.enabled !== false, // Default to true
  });
}

export function useNotificationStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['notification-stats'],
    queryFn: fetchNotificationStats,
    refetchInterval: 30000,
    enabled: options?.enabled !== false, // Default to true
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
  });
}

// Mutation hooks
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const res = await fetch(`${BASE_URL}/notifications/${notificationId}/read`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to mark notification as read');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE_URL}/notifications/read-all`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to mark all as read');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
  });
}

export function useArchiveNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const res = await fetch(`${BASE_URL}/notifications/${notificationId}/archive`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to archive notification');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const res = await fetch(`${BASE_URL}/notifications/${notificationId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete notification');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preferences: Partial<NotificationPreferences>) => {
      const res = await fetch(`${BASE_URL}/notifications/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences),
      });
      if (!res.ok) throw new Error('Failed to update preferences');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });
}

export function usePerformNotificationAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { notificationId: string; action: string }) => {
      const res = await fetch(`${BASE_URL}/notifications/${params.notificationId}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: params.action }),
      });
      if (!res.ok) throw new Error('Failed to perform action');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
  });
}

// Helper constants
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  load_status: 'Load Status',
  driver_status: 'Driver Status',
  document_required: 'Document Required',
  compliance_alert: 'Compliance Alert',
  payment_received: 'Payment Received',
  invoice_overdue: 'Invoice Overdue',
  maintenance_due: 'Maintenance Due',
  system_alert: 'System Alert',
  message: 'Message',
  mention: 'Mention',
  schedule_deviation: 'Schedule Deviation',
  eta_update: 'ETA Update',
};

export const NOTIFICATION_TYPE_ICONS: Record<NotificationType, string> = {
  load_status: 'Package',
  driver_status: 'UserCircle',
  document_required: 'FileWarning',
  compliance_alert: 'AlertTriangle',
  payment_received: 'DollarSign',
  invoice_overdue: 'AlertCircle',
  maintenance_due: 'Wrench',
  system_alert: 'Bell',
  message: 'MessageSquare',
  mention: 'AtSign',
  schedule_deviation: 'Clock',
  eta_update: 'Navigation',
};

export function getNotificationPriorityColor(priority: NotificationPriority): string {
  switch (priority) {
    case 'urgent': return 'red';
    case 'high': return 'orange';
    case 'normal': return 'blue';
    case 'low': return 'gray';
    default: return 'gray';
  }
}

export function formatNotificationTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
